/**
 * Enterprise-Shell: Sticky-Glass-Header + optionale Sidebar + Main.
 * Theme- und Density-Praeferenzen sind auf Desktop und Mobile erreichbar.
 */
import { type ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Backpack, LogOut, Menu, X, Command, Rows3, Rows2, Square, Palette, Trophy, FileCode2,
} from 'lucide-react';
import { clearAuthenticatedSessionMarker, useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { useDensity } from '@/lib/density';
import { useDevSession } from '@/lib/devSession';
import { useBotAdminSession } from '@/lib/botAdminSession';
import { useHotkey, MOD_LABEL } from '@/lib/hotkeys';
import { CommandPalette } from '@/components/CommandPalette';
import { DevLoginPanel } from '@/components/DevLoginPanel';
import { BotAdminLoginPanel } from '@/components/BotAdminLoginPanel';
import { NitradoDriftBanner } from '@/components/NitradoDriftBanner';
import { Tooltip } from '@/components/ui/Tooltip';
import { Kbd } from '@/components/ui/Kbd';
import { LOADOUT_CREATOR_ROUTE } from '@/lib/publicRoutes';

interface ShellProps {
  title: string;
  back?: string;
  sidebar?: ReactNode;
  children: ReactNode;
  /** Page-2-Slots bekommen standardmaessig den gemeinsamen PvP-Ranglisten-Shortcut. */
  slotRankingsShortcut?: boolean;
  /** Shared grouped slot sidebars already contain Ignolist and Rangliste. */
  slotNavigationShortcuts?: boolean;
}

function useMinViewportWidth(minWidthPx: number): boolean {
  const query = `(min-width: ${minWidthPx}px)`;
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatches(mql.matches);
    onChange();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

export function Shell({ title, back, sidebar, children, slotRankingsShortcut = true, slotNavigationShortcuts = true }: ShellProps) {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { density, cycle } = useDensity();
  const devActive = useDevSession().active;
  const botAdminActive = useBotAdminSession().active;
  const elevated = devActive || botAdminActive;
  const loc = useLocation();
  const serverSlotMatch = /^\/servers\/([^/]+)\/server\/([1-5])(?:\/|$)/.exec(loc.pathname);
  const serverDriftScope = serverSlotMatch
    ? { guildId: serverSlotMatch[1], slot: serverSlotMatch[2] }
    : null;
  let dashboardPage: '1' | '2' | undefined;
  if (serverSlotMatch) dashboardPage = '2';
  else if (/^\/servers\/[^/]+\/?$/.test(loc.pathname)) dashboardPage = '1';
  const activeTab = new URLSearchParams(loc.search).get('tab');
  const rankingActive = activeTab === 'rankings';
  const ignoreListActive = activeTab === 'ignorelist';
  const rankingHref = serverSlotMatch ? `${loc.pathname}?tab=rankings` : null;
  const ignoreListHref = serverSlotMatch ? `${loc.pathname}?tab=ignorelist` : null;
  const showSlotShortcuts = Boolean(slotNavigationShortcuts && sidebar && dashboardPage === '2' && serverSlotMatch);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const isDesktop = useMinViewportWidth(768);
  // Unter 640 px ist die Kopfzeile auf Guild-/Slot-Seiten (Menue + Zurueck + Marke)
  // bereits voll; der Creator-Einstieg sitzt dort in der Titelzeile darunter.
  const headerHasRoomForCreator = useMinViewportWidth(640);

  useHotkey('mod+k', event => {
    event.preventDefault();
    if (elevated) setPaletteOpen(open => !open);
  }, { allowInInputs: true });
  useHotkey('escape', () => setPaletteOpen(false), { allowInInputs: true });

  useEffect(() => { setSidebarOpen(false); }, [loc.pathname, loc.search]);
  useEffect(() => {
    const onResize = (): void => { if (window.innerWidth >= 768) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  async function logout(): Promise<void> {
    try { await fetch('/auth/logout', { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
    clearAuthenticatedSessionMarker();
    window.location.href = '/login';
  }

  const DensityIcon = density === 'compact' ? Rows3 : density === 'cozy' ? Rows2 : Square;
  const themeLabel = theme === 'obsidian' ? 'Obsidian' : 'Ice';
  const nextThemeLabel = theme === 'obsidian' ? 'Ice' : 'Obsidian';
  // Globaler Einstieg in den eigenstaendigen, oeffentlichen Loadout Creator: reiner Link auf
  // dieselbe Route wie die Startseite, ohne Guild-/Slot-Kontext und ohne Slot-Reiter. Er wird
  // genau einmal gerendert (Kopfzeile ab 640 px, sonst Titelzeile), nie in der Slot-Sidebar.
  const loadoutCreatorShortcut = (
    <Tooltip content="Loadout Creator · eigenständig, ohne Serverbezug">
      <Link
        to={LOADOUT_CREATOR_ROUTE}
        className={`inline-flex shrink-0 items-center justify-center gap-1.5 h-11 min-w-11 px-2 rounded-md text-muted hover:text-white hover:bg-bg-elev focus-ring ${
          headerHasRoomForCreator ? 'md:h-9 md:min-w-9' : ''
        }`}
        aria-label="Loadout Creator"
        data-testid="shell-loadout-creator-link"
      >
        <Backpack className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className={`text-xs font-semibold ${headerHasRoomForCreator ? 'hidden lg:inline' : ''}`}>Loadout Creator</span>
      </Link>
    </Tooltip>
  );
  const slotShortcuts = showSlotShortcuts && ignoreListHref ? (
    <div className="pt-2 mt-2 border-t border-border/60 space-y-1" data-slot-page2-shortcuts>
      <Link
        to={ignoreListHref}
        data-slot-ignorelist-shortcut
        className={`w-full text-left px-3 py-2 rounded-md inline-flex items-center gap-2 transition-colors focus-ring ${
          ignoreListActive
            ? 'bg-accent/20 text-accent border border-accent/25'
            : 'text-muted hover:bg-bg-elev hover:text-white border border-transparent'
        }`}
        aria-current={ignoreListActive ? 'page' : undefined}
      >
        <FileCode2 className="h-4 w-4" />
        Ignolist Generator
      </Link>
      {slotRankingsShortcut && rankingHref && (
        <Link
          to={rankingHref}
          data-slot-rankings-shortcut
          className={`w-full text-left px-3 py-2 rounded-md inline-flex items-center gap-2 transition-colors focus-ring ${
            rankingActive
              ? 'bg-accent/20 text-accent border border-accent/25'
              : 'text-muted hover:bg-bg-elev hover:text-white border border-transparent'
          }`}
          aria-current={rankingActive ? 'page' : undefined}
        >
          <Trophy className="h-4 w-4" />
          Rangliste
        </Link>
      )}
    </div>
  ) : null;

  return (
    <div className="dashboard-shell min-h-full flex flex-col" data-dashboard-theme={theme} data-dashboard-page={dashboardPage}>
      <header className="nexus-header sticky top-0 z-40 h-16 glass header-premium flex items-center justify-between px-3 sm:px-6">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {sidebar && (
            <button
              type="button"
              onClick={() => setSidebarOpen(open => !open)}
              className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-white hover:bg-bg-elev focus-ring shrink-0"
              aria-label={sidebarOpen ? 'Menue schliessen' : 'Menue oeffnen'}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          )}
          {back && (
            <Link
              to={back}
              className="text-muted hover:text-white inline-flex min-h-11 md:min-h-0 items-center gap-1 focus-ring rounded-md px-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm hidden sm:inline">Zurueck</span>
            </Link>
          )}
          <Link to="/servers" className="nexus-brand flex min-h-11 md:min-h-0 items-center gap-2.5 focus-ring rounded-xl px-1 group shrink-0" aria-label="V-Bot">
            <span className="nexus-brand-mark" aria-hidden="true">VB</span>
            <span className="nexus-brand-copy">
              <span className="text-white font-semibold tracking-tight">V-Bot</span>
              <small className="hidden lg:block">Discord Control</small>
            </span>
          </Link>
          <span className="text-white/15 hidden sm:inline">•</span>
          <span className="nexus-shell-title text-white/85 text-sm font-medium truncate hidden sm:inline">{title}</span>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 text-sm shrink-0">
          {isDesktop && (
            <>
              <DevLoginPanel />
              <BotAdminLoginPanel />
            </>
          )}

          <Tooltip content={<span>Befehlspalette · <Kbd>{MOD_LABEL}</Kbd>+<Kbd>K</Kbd></span>}>
            {elevated ? (
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="hidden md:inline-flex items-center gap-2 h-9 pl-2.5 pr-2 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-bg-elev/60 text-muted hover:text-white focus-ring"
                aria-label="Befehlspalette oeffnen"
              >
                <Command className="h-3.5 w-3.5" />
                <span className="text-xs">Suchen</span>
                <span className="inline-flex items-center gap-0.5"><Kbd>{MOD_LABEL}</Kbd><Kbd>K</Kbd></span>
              </button>
            ) : <span className="hidden" />}
          </Tooltip>

          {headerHasRoomForCreator && loadoutCreatorShortcut}

          <Tooltip content={`Farbschema: ${themeLabel} · zu ${nextThemeLabel} wechseln`}>
            <button
              type="button"
              onClick={toggleTheme}
              className="theme-toggle-control inline-flex items-center justify-center sm:justify-start gap-1.5 h-11 min-w-11 md:h-9 md:min-w-[92px] px-2 rounded-md text-muted hover:text-white focus-ring border border-border/60 bg-bg-elev/55"
              aria-label={`Farbschema auf ${nextThemeLabel} umschalten`}
              data-testid="theme-toggle"
            >
              <Palette className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline text-xs font-semibold">{themeLabel}</span>
            </button>
          </Tooltip>

          <Tooltip content={`Dichte: ${density}`}>
            <button
              type="button"
              onClick={cycle}
              className="inline-flex items-center justify-center h-11 w-11 md:h-9 md:w-9 rounded-md text-muted hover:text-white hover:bg-bg-elev focus-ring"
              aria-label={`Dichte umschalten (aktuell: ${density})`}
            >
              <DensityIcon className="h-4 w-4" />
            </button>
          </Tooltip>

          {user && (
            <span className="nexus-user-chip hidden lg:inline-flex" title={user.username}>
              <span aria-hidden="true">{user.username.charAt(0).toUpperCase()}</span>
              <span className="truncate max-w-[120px]">{user.username}</span>
            </span>
          )}
          <Tooltip content="Logout">
            <button
              onClick={logout}
              className="text-muted hover:text-white inline-flex items-center justify-center h-11 w-11 md:h-9 md:w-9 rounded-md hover:bg-bg-elev focus-ring"
              type="button"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </header>

      {/* Mobile Titelzeile: immer gerendert, weil sie unter 640 px den globalen Creator-Einstieg traegt. */}
      <div className="sm:hidden border-b border-border px-4 py-0.5 text-sm bg-bg-card/35 flex min-h-11 items-center justify-between gap-3">
        <span className="text-white truncate min-w-0">{title}</span>
        {!headerHasRoomForCreator && loadoutCreatorShortcut}
      </div>

      {!isDesktop && user && (
        <div className="md:hidden border-b border-border bg-bg-card/55 px-4 py-3 flex flex-col gap-3">
          <DevLoginPanel />
          <BotAdminLoginPanel />
          {elevated && (
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] text-muted hover:bg-bg-elev/60 hover:text-white focus-ring"
              aria-label="Befehlspalette oeffnen"
            >
              <Command className="h-4 w-4" />
              <span className="text-sm font-medium">Befehlspalette</span>
            </button>
          )}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden relative z-10">
        {sidebar && (
          <>
            <aside
              className="dashboard-sidebar hidden md:block w-64 lg:w-72 border-r border-border/60 backdrop-blur-md p-5 overflow-y-auto text-[15px]"
              aria-label="Navigation"
            >
              {sidebar}
              {slotShortcuts}
            </aside>
            {sidebarOpen && (
              <div
                className="md:hidden fixed inset-0 z-30 bg-black/60 animate-fade-in"
                onClick={() => setSidebarOpen(false)}
                role="presentation"
              >
                <aside
                  className="absolute left-0 top-16 bottom-0 w-72 max-w-[85vw] glass border-r border-border p-4 overflow-y-auto"
                  onClick={event => event.stopPropagation()}
                  role="dialog"
                  aria-label="Navigation"
                >
                  {sidebar}
                  {slotShortcuts}
                </aside>
              </div>
            )}
          </>
        )}
        <main className="dashboard-main flex-1 overflow-y-auto p-4 sm:p-6" role="main">
          {serverDriftScope && (
            <NitradoDriftBanner guildId={serverDriftScope.guildId} slot={serverDriftScope.slot} />
          )}
          {children}
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
